import { IconChartBar, IconUsers, IconUsersGroup } from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useStaffGuard } from "@/components/admin/staff-guard";
import { Loader } from "@/components/Loader";
import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { useFetchAdminTeamDetail } from "@/components/queries/admin";
import { Badge } from "@/components/ui/badge";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

export function formatUsageLimit(limit?: number | null): string {
	if (limit === null || limit === undefined) {
		return "Unlimited";
	}
	return String(limit);
}

export function AdminTeamDetail() {
	const { isStaff, isLoading } = useStaffGuard();
	const { teamId } = Route.useParams();
	const { teamDetail, isLoadingTeamDetail } = useFetchAdminTeamDetail(teamId);

	if (isLoading || isLoadingTeamDetail || !isStaff) {
		return <Loader />;
	}

	return (
		<PageLayout>
			<PageHeader
				breadcrumbItems={[
					{ title: "Admin", href: "/admin" },
					{ title: "Teams", href: "/admin/teams" },
					{ title: teamDetail?.name ?? "Team", href: null },
				]}
				title={teamDetail?.name ?? "Team"}
				description="Staff view of this team"
				icon={<IconUsersGroup className="size-6 text-primary" aria-hidden />}
			/>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<CardTitle>Overview</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 text-sm">
						<div>
							<dt className="text-muted-foreground">Type</dt>
							<dd className="font-medium capitalize">{teamDetail?.type}</dd>
						</div>
						<div>
							<dt className="text-muted-foreground">Members</dt>
							<dd className="font-medium">{teamDetail?.member_count ?? 0}</dd>
						</div>
						<div>
							<dt className="text-muted-foreground">Plan</dt>
							<dd className="font-medium flex items-center gap-1.5">
								{teamDetail?.plan_name || "—"}
								{teamDetail?.subscription_status && (
									<Badge
										variant={
											teamDetail.subscription_status === "active" ||
											teamDetail.subscription_status === "trialing"
												? "secondary"
												: "outline"
										}
									>
										{teamDetail.subscription_status}
									</Badge>
								)}
							</dd>
						</div>
						<div>
							<dt className="text-muted-foreground">Created</dt>
							<dd className="font-medium">
								{teamDetail?.created_at
									? new Date(teamDetail.created_at).toLocaleString()
									: "—"}
							</dd>
						</div>
					</dl>
				</CardContent>
			</Card>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<CardTitle className="flex items-center gap-2">
						<IconUsers className="h-5 w-5" aria-hidden />
						Members
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Member</TableHead>
								<TableHead>Email</TableHead>
								<TableHead>Role</TableHead>
								<TableHead>Joined</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{(teamDetail?.members ?? []).length === 0 && (
								<TableRow>
									<TableCell
										colSpan={4}
										className="text-center text-muted-foreground py-8"
									>
										No members.
									</TableCell>
								</TableRow>
							)}
							{(teamDetail?.members ?? []).map((member) => (
								<TableRow key={member.id}>
									<TableCell>
										<Link
											to="/admin/users/$userId"
											params={{ userId: member.user_id ?? "" }}
											className="font-medium hover:underline"
										>
											{[member.first_name, member.last_name]
												.filter(Boolean)
												.join(" ") || member.email}
										</Link>
									</TableCell>
									<TableCell>{member.email}</TableCell>
									<TableCell>{member.role}</TableCell>
									<TableCell className="text-muted-foreground">
										{member.created_at
											? new Date(member.created_at).toLocaleDateString()
											: "—"}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<CardTitle className="flex items-center gap-2">
						<IconChartBar className="h-5 w-5" aria-hidden />
						Usage
					</CardTitle>
					<CardDescription>
						Feature usage in the current billing period
					</CardDescription>
				</CardHeader>
				<CardContent className="pt-0">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Feature</TableHead>
								<TableHead>Usage</TableHead>
								<TableHead>Limit</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{(teamDetail?.usage ?? []).length === 0 && (
								<TableRow>
									<TableCell
										colSpan={3}
										className="text-center text-muted-foreground py-8"
									>
										No usage records.
									</TableCell>
								</TableRow>
							)}
							{(teamDetail?.usage ?? []).map((item) => (
								<TableRow key={item.feature_key}>
									<TableCell className="font-medium">
										{item.feature_key}
									</TableCell>
									<TableCell>{item.usage}</TableCell>
									<TableCell>{formatUsageLimit(item.limit)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<CardTitle>Recent Activity</CardTitle>
					<CardDescription>Last 20 team audit log events</CardDescription>
				</CardHeader>
				<CardContent className="pt-0">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Action</TableHead>
								<TableHead>IP Address</TableHead>
								<TableHead>When</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{(teamDetail?.recent_audit_logs ?? []).length === 0 && (
								<TableRow>
									<TableCell
										colSpan={3}
										className="text-center text-muted-foreground py-8"
									>
										No recent activity.
									</TableCell>
								</TableRow>
							)}
							{(teamDetail?.recent_audit_logs ?? []).map((log) => (
								<TableRow key={log.id}>
									<TableCell className="font-medium">{log.action}</TableCell>
									<TableCell>{log.ip_address}</TableCell>
									<TableCell className="text-muted-foreground">
										{log.created_at
											? new Date(log.created_at).toLocaleString()
											: "—"}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>
		</PageLayout>
	);
}

/* istanbul ignore start -- TanStack Router file route registration */
export const Route = createFileRoute("/_authorized/admin/teams/$teamId")({
	component: AdminTeamDetail,
});
/* istanbul ignore end */
